<script setup lang="ts">
  import { usePronunciation } from '@/composables/usePronunciation'
  import { computed } from 'vue'
  import { usePlaySound } from '@/composables/usePlaySound'
  import VgsButton from '@/components/common/VgsButton.vue'
  import VolumeIcon from '@/components/icons/VolumeIcon.vue'
  import VolumeSlashIcon from '@/components/icons/VolumeSlashIcon.vue'
  import RichTextRenderer from '@/components/common/RichTextRenderer.vue'
  import Gloss from '@/components/common/Gloss.vue'

  const props = defineProps<{
    pronunciationId: number,
  }>()

  const { isPlaying, toggleSound, audioElement } = usePlaySound()
  const audioUri = computed(() => audio.value?.metadata?.elementURI)
  const soundIcon = computed(() => isPlaying.value ? VolumeSlashIcon : VolumeIcon)

  const { pronunciation, audio, word } = usePronunciation(props.pronunciationId)
</script>

<template>
  <div v-if="pronunciation?.contentId" class="pronunciation-box">
    <div class="title">
      <VgsButton
        v-if="!!audioUri"
        data-testid="audio-button"
        :aria-label="isPlaying ? 'Pause lyd' : 'Spill av lyd'"
        shape="round"
        size="large"
        @click="toggleSound"
      >
        <audio ref="audioElement" :src="audio?.metadata?.elementURI" />
        <component :is="soundIcon" />
      </VgsButton>
      <span>
        {{ pronunciation?.title }}
      </span>
    </div>
    <div class="description">
      <RichTextRenderer :rich-text="pronunciation?.description ?? ''" />
    </div>
    <Gloss v-if="word" :element="word" />
  </div>
</template>

<style lang="scss" scoped>
  .pronunciation-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
    min-height: 180px;
    padding-top: 48px;

    .title {
      display: flex;
      align-items: center;
      justify-content: center;

      span {
        font-weight: 700;
        font-size: 4rem;
      }

      svg {
        width: 32px;
        height: 32px;
        fill: var(--color-blue-50)
      }
    }

    .description {
      :deep(.cms-p) {
        font-size: 1rem;
      }
    }

    .gloss {
      background-color: var(--color-green-10);
      padding-top: 0;
      padding-bottom: 0;
      min-height: 0;
      margin-top: 24px;
      font-size: 1rem;
    }
  }

  .pronunciation-box:nth-child(6n+1) {
    background-color: var(--color-green);
  }

  .pronunciation-box:nth-child(6n+2) {
    background-color: var(--color-green-20);
  }

  .pronunciation-box:nth-child(6n+3) {
    background-color: var(--color-coral-30);
  }

  .pronunciation-box:nth-child(6n+4) {
    background-color: var(--color-orange-30);
  }

  .pronunciation-box:nth-child(6n+5) {
    background-color: var(--color-red-10);
  }

  .pronunciation-box:nth-child(6n) {
    background-color: var(--color-coral-20);
  }

  </style>
